import React from 'react';
import {
  Button, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from 'react-query';
import { requestError } from './messages';
import type { ApiData, SharePointFile } from './types';

const API = 'api/integrations/sharepoint';

interface ReplacementsSectionProps {
  workspaceId: number;
  replacementCount: number;
  onUpdated: () => Promise<void>;
}

interface ReplacementDecision {
  file: SharePointFile;
  action: 'corrected_copy' | 'new_document';
}

// New files that arrived in a tenant folder right after its lease file was removed: corrected copy, or new document?
export default function ReplacementsSection({
  workspaceId, replacementCount, onUpdated,
}: ReplacementsSectionProps): JSX.Element | null {
  const { enqueueSnackbar } = useSnackbar();
  const { data: files = [] } = useQuery(
    ['get/sharepoint-items', workspaceId, 'replacements'],
    async () => axios({ url: `${API}/items/`, method: 'get', params: { workspace: workspaceId, replacements: 'true' } }),
    { enabled: replacementCount > 0, select: (res: AxiosResponse<ApiData<SharePointFile[]>>) => res.data.data },
  );
  const { mutate: decide, isLoading: deciding } = useMutation(
    async ({ file, action }: ReplacementDecision) => axios({
      url: `${API}/items/${file.id}/replacement/`, method: 'post', params: { workspace: workspaceId }, data: { action },
    }),
    {
      onSuccess: async (_res: AxiosResponse, { file, action }: ReplacementDecision): Promise<void> => {
        enqueueSnackbar(action === 'corrected_copy'
          ? `Reprocessing ${file.replaces?.lease_tenant_name ?? 'the lease'} with "${file.name}". This takes a few minutes.`
          : `Importing "${file.name}" as a new document. It will appear on the Leases page shortly.`);
        await onUpdated();
      },
      onError: (error: unknown): void => { enqueueSnackbar(requestError(error, 'handle the file'), { variant: 'error' }); },
    },
  );

  if (replacementCount === 0 || files.length === 0) {
    return null;
  }
  return (
    <>
      <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
        {`Possibly a corrected copy (${files.length})`}
      </Typography>
      <Typography color='text.secondary' sx={{ mb: 1 }}>
        Each of these files arrived in a tenant folder right after that folder&apos;s lease file was removed. Strada is holding it so it doesn&apos;t become a second lease.
      </Typography>
      <Table size='small' sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>New file</TableCell>
            <TableCell>Removed file</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id}>
              <TableCell>
                <Link href={file.web_url} target='_blank' rel='noopener noreferrer'>{file.name}</Link>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>{file.path}</Typography>
              </TableCell>
              <TableCell>
                {file.replaces?.name ?? '—'}
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                  {`${file.replaces?.lease_tenant_name ?? 'Unknown lease'} · removed from SharePoint`}
                </Typography>
              </TableCell>
              <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                <Button size='small' variant='outlined' disabled={deciding} sx={{ mr: 1 }} onClick={(): void => { decide({ file, action: 'corrected_copy' }); }}>
                  Corrected copy
                </Button>
                <Button size='small' disabled={deciding} onClick={(): void => { decide({ file, action: 'new_document' }); }}>
                  New document
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
